import React, { useState, useEffect } from "react"
import { DatePicker, Select, Spin, message, TimePicker, Button, Form, Card, InputNumber } from "antd"
import moment from "moment"
import { useSelector } from "react-redux"
import { translations } from "../../utils/constants"
import { secureApi } from "../../Config/api"
import { POST } from "../../utils/apis"
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons"
import { convertItalianToLocalTime, convertLocalToItalianTime, validateBreakTimesAdminAddLogs } from "../../utils/helpers"

const { Option } = Select

const AddEmployeeWorkLogInAdmin = ({ language, onSuccess }) => {
    const employeeNameList = useSelector((state) => state.employeeLogReducer.nameList)
    const [selectedDate, setSelectedDate] = useState(null)
    const [selectedEmployee, setSelectedEmployee] = useState(null)
    const [loading, setLoading] = useState(false)
    const [subLoading, setSubLoading] = useState(false)
    const [fetchLog, setFetchLog] = useState(null)
    const [form] = Form.useForm()

    const disableFutureDates = (current) =>
        current && current >= moment().startOf("day")

    useEffect(() => {
        if (selectedDate && selectedEmployee) triggerApiCall()
        else setFetchLog(null)
    }, [selectedDate, selectedEmployee])

    const triggerApiCall = async () => {
        try {
            setLoading(true)
            const payload = {
                employee_id: selectedEmployee.id,
                work_day: selectedDate.format("YYYY-MM-DD"),
            }
            const res = await secureApi.post(
                POST?.GET_EMPLOYEE_WORK_LOG_AND_BREAKS,
                payload
            )
            if (res?.success) {
                if (res?.work_log) {
                    setFetchLog({ workLog: res.work_log, breaks: res.breaks || [] })
                } else {
                    setFetchLog(null)
                    message.warning(
                        translations[language]?.noRecordFoundPleaseAddManually
                    )
                }
            } else {
                setFetchLog(null)
            }
        } catch (err) {
            console.error("API Error:", err)
            message.error(translations[language].failedToFetchEmployeeEntry)
        } finally {
            setLoading(false)
        }
    }

    const handleManualSubmit = async (values) => {
        try {
            const entry = values.entry_time
            const exit = values.exit_time
            const breaks = values.breaks || []

            if (!entry || !exit) {
                return message.error(translations[language].pleaseSelectBothEntryAndExitTime)
            }

            if (entry.format("HH:mm") === exit.format("HH:mm")) {
                return message.error(
                    translations?.[language]?.entryTimeAndExittTimeCannotBeTheSame
                )
            }

            if (entry.isAfter(exit)) {
                return message.error(
                    translations?.[language]?.entryTimeMustBeBeforeExitTime
                )
            }

            const err = validateBreakTimesAdminAddLogs(breaks, entry, exit)
            if (err) return message.error(err)

            const italianEntry = convertLocalToItalianTime(entry, selectedDate)
            const italianExit = convertLocalToItalianTime(exit, selectedDate)

            const payload = {
                employee_id: selectedEmployee.id,
                employee_name: selectedEmployee.user_name,

                // Important: use Italy converted date
                work_day: italianEntry.format("YYYY-MM-DD"),

                entry_time: italianEntry.format("HH:mm:ss"),
                exit_time: italianExit.format("HH:mm:ss"),

                breaks: breaks.map((b, i) => {
                    const italianStart = convertLocalToItalianTime(b.start, selectedDate)
                    const italianEnd = convertLocalToItalianTime(b.end, selectedDate)

                    return {
                        number: i + 1,
                        start_time: italianStart.format("HH:mm:ss"),
                        end_time: italianEnd.format("HH:mm:ss"),
                    }
                }),

                dinner: values?.dinner,
                extra: values?.extra,
                ferry_expense: values?.ferry_expense,
                hotel: values?.hotel,
                kilometers: values?.kilometers,
                kmRate: values?.kmRate,
                lunch: values?.lunch,
                telepass: values?.telepass,
                travelExpenses: values?.travelExpenses,
            }

            setSubLoading(true)

            const res = await secureApi.post(
                POST?.ADD_EMPLOYEE_WORK_LOG_IN_ADMIN_WITH_BREAKS,
                payload
            )

            if (res?.success) {
                message.success(translations[language]?.workLogAddedSuccessfully)
                onSuccess?.()
            }
            else {
                message.warning(res?.message)
            }
        }
        catch (error) {
            message.error(error.message || "Failed to add work log")
        }
        finally {
            setSubLoading(false)
        }
    }


    return (
        <div className="timesheet-container">
            <div className="timesheet-wrapper">
                <div className="timesheet-grid form-card" style={{ maxWidth: 720 }}>
                    <Form
                        className="add-employee-work-log-form"
                        form={form}
                        layout="vertical"
                        onFinish={handleManualSubmit}
                        onValuesChange={() => form.validateFields()}
                        initialValues={{ kmRate: "0.35" }}
                    >
                        <div className="form-group">
                            <h2 className="section-header">
                                {translations[language]?.PersonalInfo}
                            </h2>

                            <Form.Item label={translations[language]?.SelectWorkDate} required className="add-employee-date-picker">
                                <DatePicker
                                    disabledDate={disableFutureDates}
                                    format="YYYY-MM-DD"
                                    style={{ width: "100%" }}
                                    value={selectedDate}
                                    onChange={(date) => setSelectedDate(date)}
                                    placeholder={translations[language]?.pleaseSelectDate}
                                />
                            </Form.Item>

                            <Form.Item
                                label={translations[language]?.selectEmployeeToCheckIn}
                                required
                            >
                                <Select
                                    showSearch
                                    placeholder={translations[language]?.selectEmployees}
                                    style={{ width: "100%" }}
                                    value={selectedEmployee?.user_name}
                                    size="large"
                                    onChange={(val) => {
                                        const emp = employeeNameList?.find(
                                            (e) => e.user_name === val
                                        )
                                        setSelectedEmployee(emp)
                                    }}
                                    filterOption={(input, option) =>
                                        option?.children
                                            .toLowerCase()
                                            .includes(input.toLowerCase())
                                    }
                                >
                                    {employeeNameList?.map((emp) => (
                                        <Option key={emp.id} value={emp.user_name}>
                                            {emp.user_name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </div>

                        {loading && (
                            <div style={{ marginTop: 20, textAlign: "center" }}>
                                <Spin tip="Loading..." />
                            </div>
                        )}

                        {!loading && fetchLog && (
                            <Card
                                style={{
                                    marginTop: 20,
                                    backgroundColor: "#fff2f0",
                                    borderRadius: 6,
                                }}
                            >
                                <p>
                                    <b>{fetchLog.workLog.employee_name}</b> {translations[language]?.workedOn}{" "}
                                    <b>
                                        {moment(fetchLog.workLog.work_day).format("DD-MM-YYYY")}
                                    </b>
                                </p>
                                {(() => {
                                    const entryMoment = convertItalianToLocalTime(
                                        fetchLog?.workLog?.entry_time
                                    )
                                    const exitMoment = convertItalianToLocalTime(
                                        fetchLog?.workLog?.exit_time
                                    )

                                    if (!entryMoment?.isValid() || !exitMoment?.isValid())
                                        return (
                                            <p>
                                                {translations[language]?.currentlyOnWorkingEntryTime}:{" "}
                                                {entryMoment?.format("HH:mm")}
                                            </p>
                                        )

                                    let totalBreakSeconds = 0
                                    if (Array.isArray(fetchLog.breaks)) {
                                        fetchLog.breaks.forEach((b) => {
                                            const start = moment(b.start_time, "HH:mm:ss")
                                            const end = moment(b.end_time, "HH:mm:ss")
                                            if (start.isValid() && end.isValid()) {
                                                totalBreakSeconds += Math.max(
                                                    0,
                                                    end.diff(start, "seconds")
                                                )
                                            }
                                        })
                                    }

                                    const diffSeconds =
                                        exitMoment.diff(entryMoment, "seconds") - totalBreakSeconds
                                    const h = Math.floor(diffSeconds / 3600)
                                    const m = Math.floor((diffSeconds % 3600) / 60)

                                    return (
                                        <div
                                            style={{
                                                background: "#ffccc7",
                                                color: "#000",
                                                borderRadius: 4,
                                                padding: 8,
                                            }}
                                        >
                                            <p>
                                                {translations[language].EntryTime}:{" "}
                                                {entryMoment.format("HH:mm")}
                                            </p>
                                            <p>
                                                {translations[language].ExitTime}:{" "}
                                                {exitMoment.format("HH:mm")}
                                            </p>
                                            <p>
                                                {translations[language].totalWorked}: {h}h {m}m
                                            </p>
                                        </div>
                                    )
                                })()}
                            </Card>
                        )}

                        {!loading && !fetchLog && selectedEmployee && selectedDate && (
                            <>
                                <div className="form-group">
                                    <h2 className="section-header">
                                        {translations[language]?.WorkingHours}
                                    </h2>

                                    <Form.Item
                                        label={translations[language]?.EntryTime}
                                        name="entry_time"
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    translations?.[language]?.pleaseSelectEntryTime,
                                            },
                                        ]}
                                    >
                                        <TimePicker placeholder={translations[language]?.selectTime} format="HH:mm" style={{ width: "100%", borderRadius: 6, height: 40 }} />
                                    </Form.Item>

                                    <Form.Item
                                        label={translations[language]?.ExitTime}
                                        name="exit_time"
                                        dependencies={["entry_time"]}
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    translations?.[language]?.pleaseSelectExitTime,
                                            },
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    const entry = getFieldValue("entry_time")
                                                    if (!entry || !value) return Promise.resolve()
                                                    if (value.isSame(entry)) {
                                                        return Promise.reject(
                                                            new Error(
                                                                translations?.[language]
                                                                    ?.entryTimeAndExittTimeCannotBeTheSame
                                                            )
                                                        )
                                                    }
                                                    if (!value.isAfter(entry)) {
                                                        return Promise.reject(
                                                            new Error(
                                                                translations?.[language]
                                                                    ?.exitTimeMustBeGreaterThanEntryTime
                                                            )
                                                        )
                                                    }
                                                    return Promise.resolve()
                                                },
                                            }),
                                        ]}
                                    >
                                        <TimePicker placeholder={translations[language]?.selectTime} format="HH:mm" style={{ width: "100%", borderRadius: 6, height: 40 }} />
                                    </Form.Item>
                                </div>

                                <div className="form-group">
                                    <h2 className="section-header">
                                        {translations[language]?.breaks}
                                    </h2>

                                    <Form.List name="breaks">
                                        {(fields, { add, remove }) => (
                                            <>
                                                {fields?.map(({ key, name, ...rest }) => (
                                                    <div
                                                        key={key}
                                                        style={{
                                                            display: "flex",
                                                            gap: "10px",
                                                            marginBottom: 10,
                                                        }}
                                                    >
                                                        <Form.Item
                                                            {...rest}
                                                            name={[name, "start"]}
                                                            rules={[
                                                                {
                                                                    required: true,
                                                                    message:
                                                                        translations[language]?.startTimeRequired,
                                                                },
                                                            ]}
                                                            style={{ width: "100%" }}
                                                        >
                                                            <TimePicker
                                                                placeholder={`${translations[language]?.startBreakTime}`}
                                                                style={{ width: "100%", borderRadius: 6, height: 40 }}
                                                                format="HH:mm"
                                                            />
                                                        </Form.Item>
                                                        <Form.Item
                                                            {...rest}
                                                            name={[name, "end"]}
                                                            rules={[
                                                                {
                                                                    required: true,
                                                                    message:
                                                                        translations[language]?.endTimeRequired,
                                                                },
                                                            ]}
                                                            style={{ width: "100%" }}
                                                        >
                                                            <TimePicker
                                                                style={{ width: "100%", borderRadius: 6, height: 40 }}
                                                                placeholder={`${translations[language]?.endBreakTime}`}
                                                                format="HH:mm"
                                                            />
                                                        </Form.Item>
                                                        <MinusCircleOutlined
                                                            style={{
                                                                background: "red",
                                                                color: "white",
                                                                padding: 6,
                                                                borderRadius: 4,
                                                                height: "40px "
                                                            }}
                                                            onClick={() => remove(name)}
                                                        />
                                                    </div>
                                                ))}
                                                <Button
                                                    type="dashed"
                                                    onClick={() => add()}
                                                    icon={<PlusOutlined />}
                                                    block
                                                >
                                                    {translations[language]?.addBreak}
                                                </Button>
                                            </>
                                        )}
                                    </Form.List>
                                </div>

                                <div className="form-group">
                                    <h2 className="section-header">
                                        {translations[language]?.ExtraExpenses}
                                    </h2>

                                    <div className="form-row two-cols">
                                        <Form.Item label={translations[language]?.Lunch} name="lunch">
                                            <InputNumber min={0} precision={2} style={{ width: "100%", borderRadius: 6, height: 40 }} placeholder={translations[language]?.amount} />
                                        </Form.Item>
                                        <Form.Item label={translations[language]?.Dinner} name="dinner">
                                            <InputNumber min={0} precision={2} style={{ width: "100%", borderRadius: 6, height: 40 }} placeholder={translations[language]?.amount} />
                                        </Form.Item>
                                    </div>

                                    <div className="form-row two-cols">
                                        <Form.Item label={translations[language]?.HotelExpense} name="hotel">
                                            <InputNumber min={0} precision={2} style={{ width: "100%", borderRadius: 6, height: 40 }} placeholder={translations[language]?.amount} />
                                        </Form.Item>
                                        <Form.Item label={translations[language]?.Ferry} name="ferry_expense">
                                            <InputNumber min={0} precision={2} style={{ width: "100%", borderRadius: 6, height: 40 }} placeholder={translations[language]?.amount} />
                                        </Form.Item>
                                    </div>

                                    <div className="form-row two-cols">
                                        <Form.Item label={translations[language]?.Telepass} name="telepass">
                                            <InputNumber min={0} precision={2} style={{ width: "100%", borderRadius: 6, height: 40 }} placeholder={translations[language]?.amount} />
                                        </Form.Item>
                                        <Form.Item label={translations[language]?.Extra} name="extra">
                                            <InputNumber min={0} precision={2} style={{ width: "100%", borderRadius: 6, height: 40 }} placeholder={translations[language]?.amount} />
                                        </Form.Item>
                                    </div>

                                    <div className="form-row two-cols">
                                        <Form.Item label={translations[language]?.KMTraveled} name="kilometers">
                                            <InputNumber min={0} precision={2} style={{ width: "100%", borderRadius: 6, height: 40 }} placeholder={translations[language]?.distanceInKm} />
                                        </Form.Item>
                                        <Form.Item label={translations[language]?.KMRate} name="kmRate" initialValue={"0.35"}>
                                            <InputNumber min={0} precision={2} style={{ width: "100%", borderRadius: 6, height: 40 }} placeholder={translations[language]?.kmRatePlaceholder} />
                                        </Form.Item>
                                    </div>

                                    <Form.Item label={translations[language]?.TravelExpenses} name="travelExpenses">
                                        <InputNumber min={0} precision={2} style={{ width: "100%", borderRadius: 6, height: 40 }} placeholder={translations[language]?.amount} />
                                    </Form.Item>
                                </div>

                                <div className="button-main-div" style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                                    <Button
                                        onClick={() => {
                                            form.resetFields()
                                            onSuccess?.()
                                        }}
                                        size="large"
                                        className="cancel-button"
                                    >
                                        {translations?.[language]?.cancel}
                                    </Button>
                                    <Button
                                        type="primary"
                                        size="large"
                                        htmlType="submit"
                                        className="submit-button"
                                        loading={subLoading}
                                    >
                                        {translations?.[language]?.AddToLog}
                                    </Button>
                                </div>
                            </>
                        )}
                    </Form>
                </div>
            </div>
        </div>
    )
}

export default AddEmployeeWorkLogInAdmin
